// dashboard/views/BillingView.js
// Extracted from App.js (2026-08-12 split).

import i18n from '../../i18n';
import { useTranslation } from 'react-i18next';
import { useState, useEffect, useCallback } from 'react';
import { supabase } from '../../supabase';
import { FUNCTIONS_URL, c } from '../constants';

// Safe display fallbacks only. Managers receive the live Stripe amounts from
// get-billing-offer; Checkout remains the final authoritative total.
const MONTHLY_PRICE_PER_SEAT = 70;
const YEARLY_PRICE_PER_SEAT  = 725;

export default function BillingView({ clinic, membership, subscription, subscriptionLoaded, subscriptionError, onRefresh }) {
  const { t } = useTranslation();
  const [plan,      setPlan]      = useState('monthly');
  const [seats,     setSeats]     = useState(1);
  const [loading,   setLoading]   = useState(false);
  const [error,     setError]     = useState('');
  const [billingOffer, setBillingOffer] = useState(null);
  const [offerError, setOfferError] = useState('');

  const isBeta      = clinic?.billing_program === 'beta';
  const isActive    = subscription?.status === 'active' || subscription?.status === 'trialing';
  // past_due/unpaid clinics still have a live Stripe subscription (payment
  // failed, not canceled) — they belong in the "manage existing
  // subscription" view, not the fresh-Subscribe form, which would let them
  // start a second, parallel subscription. See create-checkout-session's
  // matching guard — 'unpaid' was missing here too (Phase 6 audit,
  // 2026-07-15), same real Stripe status as that fix.
  const isPastDue   = subscription?.status === 'past_due' || subscription?.status === 'unpaid';
  const showManage  = isActive || isPastDue;
  const canBilling  = membership?.is_manager;

  const callEdgeFunction = useCallback(async (fnName, body) => {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) throw new Error(t('billingView.sessionExpired'));
    const res = await fetch(`${FUNCTIONS_URL}/${fnName}`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${session.access_token}`, 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
    const data = await res.json();
    if (data.error) throw new Error(data.error);
    return data;
  }, [t]);

  useEffect(() => {
    if (!subscriptionLoaded || isBeta || !canBilling) return;
    let active = true;
    setOfferError('');
    callEdgeFunction('get-billing-offer', { clinic_id: clinic.id })
      .then(offer => {
        if (!active) return;
        setBillingOffer(offer);
        setSeats(current => Math.max(current, offer.minimum_seat_count || 1));
      })
      .catch(offerLoadError => {
        if (!active) return;
        console.warn('Could not load authoritative billing offer:', offerLoadError);
        setOfferError(offerLoadError?.message || 'Unknown error');
      });
    return () => { active = false; };
  }, [subscriptionLoaded, isBeta, canBilling, clinic.id, callEdgeFunction]);

  const handleSubscribe = async () => {
    setLoading(true); setError('');
    try {
      const { url } = await callEdgeFunction('create-checkout-session', {
        clinic_id: clinic.id, seat_count: seats, plan,
      });
      window.location.href = url;
    } catch (e) { setError(e.message || t('billingView.checkoutError')); setLoading(false); }
  };

  const handleManageBilling = async () => {
    setLoading(true); setError('');
    try {
      const { url } = await callEdgeFunction('create-billing-portal-session', { clinic_id: clinic.id });
      window.location.href = url;
    } catch (e) { setError(e.message || t('billingView.portalError')); setLoading(false); }
  };

  // clinical_beta_2026's discount is deterministic from beta_phase
  // (get-billing-offer computes effective_unit_amount server-side) —
  // shown here instead of the nominal Stripe price so a beta/discount_year
  // clinic sees what they're actually being charged, not the full list
  // price they never actually pay.
  const currentEffectiveUnitAmount = billingOffer?.current_subscription?.effective_unit_amount
    ?? billingOffer?.current_subscription?.price?.unit_amount;
  const betaPhase = billingOffer?.beta?.phase || null;
  // discount_year always runs one annual cycle from its own start
  // (architecture doc §G4 step 3, iterations: 1 against the annual
  // Price) — an approximation for display only; Checkout/the billing
  // portal remain the authoritative source for exact billing dates.
  const discountYearEndDate = billingOffer?.beta?.discount_year_started_at
    ? (() => {
        const end = new Date(billingOffer.beta.discount_year_started_at);
        end.setFullYear(end.getFullYear() + 1);
        return end.toLocaleDateString(i18n.language, { month: 'long', day: 'numeric', year: 'numeric' });
      })()
    : null;

  const monthlyPricePerSeat = billingOffer?.offers?.monthly?.unit_amount != null
    ? billingOffer.offers.monthly.unit_amount / 100
    : MONTHLY_PRICE_PER_SEAT;
  const yearlyPricePerSeat = billingOffer?.offers?.yearly?.unit_amount != null
    ? billingOffer.offers.yearly.unit_amount / 100
    : YEARLY_PRICE_PER_SEAT;
  const minimumSeats = billingOffer?.minimum_seat_count || 1;
  const quoteReady = Boolean(billingOffer?.offers?.monthly && billingOffer?.offers?.yearly);
  const annualSavingsPct = Math.round((1 - yearlyPricePerSeat / (monthlyPricePerSeat * 12)) * 100);
  const pricePerSeat  = plan === 'yearly' ? yearlyPricePerSeat : monthlyPricePerSeat;
  const periodLabel   = plan === 'yearly' ? t('billingView.perDoctorYearly') : t('billingView.perDoctorMonthly');
  const totalLabel    = plan === 'yearly'
    ? t('billingView.totalYearly', { amount: (yearlyPricePerSeat * seats).toLocaleString(i18n.language) })
    : t('billingView.totalMonthly', { amount: (monthlyPricePerSeat * seats).toLocaleString(i18n.language) });

  const card = { backgroundColor: c.white, borderRadius: 12, border: `1px solid ${c.border}`, padding: '28px 32px', marginBottom: 20 };
  const h2   = { fontSize: 16, fontWeight: 600, color: c.slate, margin: '0 0 16px' };
  const row  = { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 };
  const lbl  = { fontSize: 13, color: c.textSoft };
  const val  = { fontSize: 13, fontWeight: 500, color: c.slate };
  const btn  = (primary) => ({
    padding: '10px 20px', borderRadius: 8, cursor: loading ? 'default' : 'pointer',
    fontSize: 13, fontWeight: 600,
    backgroundColor: primary ? c.moss : c.white,
    color: primary ? c.white : c.slate,
    border: primary ? 'none' : `1px solid ${c.border}`,
    opacity: loading ? 0.6 : 1,
  });

  if (isBeta) return (
    <div>
      <h1 style={{ fontSize: 24, fontWeight: 700, color: c.slate, margin: '0 0 24px' }}>{t('billingView.title')}</h1>
      <div style={card}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
          <span style={{ fontSize: 13, fontWeight: 600, color: c.moss, backgroundColor: c.greenLight, padding: '2px 10px', borderRadius: 12 }}>{t('billingView.betaBadge')}</span>
        </div>
        <p style={{ fontSize: 14, color: c.slate, margin: 0, lineHeight: 1.6 }}>
          {t('billingView.betaBody', { clinicName: clinic.name })}
        </p>
      </div>
    </div>
  );

  // `subscription` starts null and stays null for a genuinely-unsubscribed
  // clinic — same value as "haven't fetched yet." Without this, an already-
  // paying clinic briefly rendered the Subscribe form instead of Current
  // Plan on every load, until the fetch resolved a moment later (2026-08-16
  // audit).
  if (!subscriptionLoaded) return (
    <div>
      <h1 style={{ fontSize: 24, fontWeight: 700, color: c.slate, margin: '0 0 24px' }}>{t('billingView.title')}</h1>
      <div style={card}>
        {subscriptionError ? (
          <>
            <p role="alert" style={{ fontSize: 13, color: c.error, margin: '0 0 14px' }}>{t('billingView.loadError')}</p>
            <button style={btn(true)} onClick={onRefresh}>{t('common.retry')}</button>
          </>
        ) : (
          <p style={{ fontSize: 13, color: c.textSoft, margin: 0 }}>{t('common.loading')}</p>
        )}
      </div>
    </div>
  );

  return (
    <div>
      <h1 style={{ fontSize: 24, fontWeight: 700, color: c.slate, margin: '0 0 24px' }}>{t('billingView.title')}</h1>

      {showManage ? (
        /* ── Active or past-due subscription ─────────────────────────────── */
        <div style={card}>
          <p style={h2}>{t('billingView.currentPlan')}</p>
          {betaPhase && betaPhase !== 'released' && (
            <>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
                <span style={{ fontSize: 13, fontWeight: 600, color: c.moss, backgroundColor: c.greenLight, padding: '2px 10px', borderRadius: 12 }}>
                  {betaPhase === 'beta' ? t('billingView.clinicalBetaBadge') : t('billingView.clinicalBetaDiscountBadge')}
                </span>
              </div>
              <p style={{ fontSize: 13, color: c.textSoft, margin: '0 0 16px', lineHeight: 1.5 }}>
                {betaPhase === 'beta'
                  ? t('billingView.clinicalBetaFreeBody', { clinicName: clinic.name })
                  : t('billingView.clinicalBetaDiscountBody', { clinicName: clinic.name, date: discountYearEndDate })}
              </p>
            </>
          )}
          {isPastDue && (
            <p style={{ fontSize: 13, color: c.error, backgroundColor: '#FDECEC', padding: '10px 14px', borderRadius: 8, margin: '0 0 16px', lineHeight: 1.5 }}>
              {t('billingView.pastDueWarning')}
            </p>
          )}
          <div style={row}>
            <span style={lbl}>{t('billingView.status')}</span>
            <span style={{
              fontSize: 13, fontWeight: 600, padding: '2px 10px', borderRadius: 12,
              color: isPastDue ? c.error : c.moss,
              backgroundColor: isPastDue ? '#FDECEC' : c.greenLight,
            }}>
              {isPastDue ? t('billingView.statusPaymentFailed') : subscription.status === 'trialing' ? t('billingView.statusTrial') : t('billingView.statusActive')}
            </span>
          </div>
          <div style={row}><span style={lbl}>{t('billingView.plan')}</span><span style={val}>{subscription.plan === 'yearly' ? t('billingView.planAnnual') : t('billingView.planMonthly')}</span></div>
          <div style={row}><span style={lbl}>{t('billingView.doctorSeatsLabel')}</span><span style={val}>{subscription.seat_count}</span></div>
          <div style={row}>
            <span style={lbl}>{subscription.plan === 'yearly' ? t('billingView.annualTotal') : t('billingView.monthlyTotal')}</span>
            <span style={val}>
              {subscription.plan === 'yearly'
                ? `$${(((currentEffectiveUnitAmount ?? billingOffer?.offers?.yearly?.unit_amount) / 100 || yearlyPricePerSeat) * (subscription.seat_count || 1)).toLocaleString(i18n.language)}/year`
                : `$${(((currentEffectiveUnitAmount ?? billingOffer?.offers?.monthly?.unit_amount) / 100 || monthlyPricePerSeat) * (subscription.seat_count || 1)).toLocaleString(i18n.language)}/month`}
            </span>
          </div>
          <div style={row}>
            <span style={lbl}>{t('billingView.nextRenewal')}</span>
            <span style={val}>
              {subscription.current_period_end
                ? new Date(subscription.current_period_end).toLocaleDateString(i18n.language, { month: 'long', day: 'numeric', year: 'numeric' })
                : '—'}
            </span>
          </div>
          {error && <p role="alert" style={{ color: c.error, fontSize: 12, margin: '12px 0 0' }}>{error}</p>}
          {canBilling && (
            <div style={{ marginTop: 20 }}>
              <button style={btn(true)} onClick={handleManageBilling} disabled={loading}>
                {loading ? t('billingView.opening') : t('billingView.manageBilling')}
              </button>
              <p style={{ fontSize: 11, color: c.textSoft, margin: '8px 0 0' }}>
                {t('billingView.manageBillingHint')}
              </p>
              <p style={{ fontSize: 11, color: c.textSoft, margin: '4px 0 0' }}>
                {t('billingView.portalSupportFallback')}
              </p>
            </div>
          )}
        </div>
      ) : (
        /* ── Subscribe form ──────────────────────────────────────────────── */
        <div style={card}>
          <p style={h2}>{t('billingView.activateSubscription')}</p>
          <p style={{ fontSize: 13, color: c.textSoft, margin: '0 0 24px', lineHeight: 1.5 }}>
            {t('billingView.subscribeBody')}
          </p>

          {/* Plan toggle */}
          <div style={{ marginBottom: 20 }}>
            <p style={{ ...lbl, marginBottom: 8 }}>{t('billingView.billingCycle')}</p>
            <div style={{ display: 'flex', gap: 8 }}>
              {['monthly', 'yearly'].map(p => (
                <button key={p} onClick={() => setPlan(p)} style={{
                  padding: '8px 18px', borderRadius: 8, fontSize: 13, fontWeight: plan === p ? 600 : 400,
                  cursor: 'pointer', border: `1px solid ${plan === p ? c.moss : c.border}`,
                  backgroundColor: plan === p ? c.greenLight : c.white,
                  color: plan === p ? c.moss : c.textSoft,
                }}>
                  {p === 'monthly'
                    ? t('billingView.monthlyOption', { price: monthlyPricePerSeat })
                    : t('billingView.annualOption', { price: yearlyPricePerSeat })}
                  {p === 'yearly' && annualSavingsPct > 0 && <span style={{ fontSize: 11, marginLeft: 6, color: c.moss }}>{t('billingView.savePercent', { pct: annualSavingsPct })}</span>}
                </button>
              ))}
            </div>
          </div>

          {/* Seat count */}
          <div style={{ marginBottom: 24 }}>
            <p style={{ ...lbl, marginBottom: 8 }}>{t('billingView.numberOfSeats')}</p>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <button disabled={seats <= minimumSeats} onClick={() => setSeats(s => Math.max(minimumSeats, s - 1))} aria-label={t('billingView.decreaseSeatsA11y')} style={{ width: 32, height: 32, borderRadius: 8, border: `1px solid ${c.border}`, background: c.white, fontSize: 18, cursor: seats <= minimumSeats ? 'default' : 'pointer', color: c.slate, opacity: seats <= minimumSeats ? 0.5 : 1 }}>−</button>
              <span style={{ fontSize: 20, fontWeight: 600, color: c.slate, minWidth: 24, textAlign: 'center' }}>{seats}</span>
              <button onClick={() => setSeats(s => s + 1)} aria-label={t('billingView.increaseSeatsA11y')} style={{ width: 32, height: 32, borderRadius: 8, border: `1px solid ${c.border}`, background: c.white, fontSize: 18, cursor: 'pointer', color: c.slate }}>+</button>
              <span style={{ fontSize: 13, color: c.textSoft }}>
                ${pricePerSeat.toLocaleString(i18n.language)}{periodLabel} · {totalLabel}
              </span>
            </div>
          </div>

          {offerError && (
            <div role="alert" style={{ fontSize: 11, color: c.sandstoneText, margin: '-12px 0 16px' }}>
              <p style={{ margin: '0 0 8px' }}>{t('billingView.quoteUnavailable')}</p>
              <button style={btn(false)} onClick={onRefresh}>{t('common.retry')}</button>
            </div>
          )}

          {error && <p role="alert" style={{ color: c.error, fontSize: 12, margin: '0 0 16px' }}>{error}</p>}

          {canBilling ? (
            <button style={btn(true)} onClick={handleSubscribe} disabled={loading || !quoteReady}>
              {loading
                ? t('billingView.redirecting')
                : !quoteReady
                  ? t('billingView.loadingQuote')
                  : t('billingView.subscribeButton', { count: seats })}
            </button>
          ) : (
            <p style={{ fontSize: 13, color: c.textSoft }}>{t('billingView.contactManager')}</p>
          )}
        </div>
      )}
    </div>
  );
}
